import { useState, useRef, ReactNode, CSSProperties, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '@/utils/store/store'
import MessageContextMenu from '@/ui/components/MessageContextMenu/MessageContextMenu'
import type { MessageContextMenuItem } from '@/ui/components/MessageContextMenu/types/MessageContextMenuTypes'
import type { OutputFeatures, MessageType } from '@/shared/types/messageTools'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import ReplyIcon from '@/assets/reply.svg'
import ReplyIconBlue from '@/assets/reply-blue.svg'

import './MessageWrapper.scss'

// The overlays in which the detail of a card is shown (see GroupMessage/Cards)
const CARD_OVERLAY_SELECTOR = '.card-overlay, .card-fullscreen-wrapper'

interface MessageWrapperProps {
    content: string;
    hasContent: boolean;
    children: ReactNode;
    align?: 'left' | 'right';
    fullWidth?: boolean;
    outputFeatures?: OutputFeatures;
    messageType: MessageType;
    isWelcomeMessage?: boolean;
    isStreaming?: boolean;
}

interface MenuItemsBuilder {
    t: (key: string) => string;
    copyIcon: string;
    content: string;
    handleCopy: (content: string) => Promise<void>;
}

const buildAssistantMenuItems = (
    outputFeatures: OutputFeatures,
    { t, copyIcon, content, handleCopy }: MenuItemsBuilder
): MessageContextMenuItem[] => {
    const items: MessageContextMenuItem[] = []

    if (outputFeatures.reply) {
        items.push({
            id: 'reply',
            icon: { type: 'svg', src: ReplyIcon, hoverSrc: ReplyIconBlue },
            label: t('chat:reply'),
            onClick: () => {
                // TODO: Implement reply handler
            },
        })
    }

    if (outputFeatures.copyToClipboard) {
        items.push({
            id: 'copy',
            icon: copyIcon,
            label: t('chat:copy'),
            onClick: () => void handleCopy(content),
        })
    }

    return items
}

const buildUserMenuItems = (
    outputFeatures: OutputFeatures,
    { t, copyIcon, content, handleCopy }: MenuItemsBuilder
): MessageContextMenuItem[] => {
    const items: MessageContextMenuItem[] = []

    if (outputFeatures.edit) {
        items.push({
            id: 'edit',
            icon: 'edit',
            label: t('chat:edit'),
            onClick: () => {
                // TODO: Implement edit handler
            },
        })
    }

    if (outputFeatures.copyToClipboard) {
        items.push({
            id: 'copy',
            icon: copyIcon,
            label: t('chat:copy'),
            onClick: () => void handleCopy(content),
        })
    }

    if (outputFeatures.delete) {
        items.push({
            id: 'delete',
            icon: 'delete',
            label: t('chat:delete'),
            onClick: () => {
                // TODO: Implement delete handler
            },
        })
    }

    return items
}

const clearLongPressTimer = (timerRef: React.RefObject<NodeJS.Timeout | null>) => {
    if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
    }
}

const buildMenuItems = (
    hasContent: boolean,
    outputFeatures: OutputFeatures | undefined,
    messageType: MessageType,
    menuBuilder: MenuItemsBuilder
): MessageContextMenuItem[] => {
    if (!hasContent || !outputFeatures) {
        return []
    }

    if (messageType === 'assistant') {
        return buildAssistantMenuItems(outputFeatures, menuBuilder)
    }

    return buildUserMenuItems(outputFeatures, menuBuilder)
}

const MessageWrapper = ({
    content,
    hasContent,
    children,
    align = 'left',
    fullWidth = true,
    outputFeatures,
    messageType,
    isWelcomeMessage = false,
    isStreaming = false,
}: MessageWrapperProps) => {
    const { t } = useTranslation()
    const isDarkTheme = useSelector((state: RootState) => state.application.darkTheme)
    const [isHovered, setIsHovered] = useState(false)
    const [isLongPressed, setIsLongPressed] = useState(false)
    const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null)
    const { copyIcon, handleCopy } = useCopyToClipboard()
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const handleTouchStart = () => {
        longPressTimerRef.current = setTimeout(() => {
            setIsLongPressed(true)
        }, 500)
    }

    const handleTouchEnd = () => {
        clearLongPressTimer(longPressTimerRef)
        setIsLongPressed(false)
    }

    const handleTouchMove = () => {
        clearLongPressTimer(longPressTimerRef)
    }

    const menuBuilder = { t, copyIcon, content, handleCopy }
    const items = buildMenuItems(hasContent, outputFeatures, messageType, menuBuilder)
    const showMenu = (isHovered || isLongPressed) && hasContent && !isWelcomeMessage && !isStreaming
    const menuClassName = align === 'right' ? '-align-right' : ''
    const wrapperClassName = `message-context-wrapper${fullWidth ? '' : ' -auto-width'}${isStreaming ? ' --streaming' : ''}`

    useEffect(() => {
        const wrapper = wrapperRef.current
        if (!showMenu || !wrapper) {
            setMenuStyle(null)
            return
        }

        // The menu is in the body, above the message: it is put again when the message moves or changes its size
        // (the accordion of a card detail opens, the window is resized), or it would stay where it was
        const positionMenu = () => {
            const rect = wrapper.getBoundingClientRect()
            const rem = parseFloat(getComputedStyle(document.documentElement).fontSize)
            // In the detail of a card (an overlay of its own) the menu has to be above that overlay
            const aboveOverlay = wrapper.closest(CARD_OVERLAY_SELECTOR)
                ? { zIndex: 'calc(var(--z-index-popover) + 1)' }
                : undefined
            setMenuStyle(
                align === 'right'
                    ? { position: 'fixed', top: rect.top - 2.5 * rem, right: window.innerWidth - rect.right + 0.75 * rem, ...aboveOverlay }
                    : { position: 'fixed', top: rect.top - 2.5 * rem, left: rect.left + 0.75 * rem, ...aboveOverlay }
            )
        }

        positionMenu()
        const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(positionMenu)
        observer?.observe(wrapper)
        window.addEventListener('resize', positionMenu)
        return () => {
            observer?.disconnect()
            window.removeEventListener('resize', positionMenu)
        }
    }, [showMenu, align])

    useEffect(() => {
        if (!showMenu) return
        // A scroll inside the message itself (the body of a card detail) does not move the message: the menu stays
        const dismiss = (event: Event) => {
            if (event.target instanceof Node && wrapperRef.current?.contains(event.target)) return
            setIsHovered(false)
            setIsLongPressed(false)
        }
        document.addEventListener('scroll', dismiss, { passive: true, capture: true })
        return () => document.removeEventListener('scroll', dismiss, { capture: true })
    }, [showMenu])

    return (
        <div
            ref={wrapperRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            className={wrapperClassName}
            role="menuitem"
            tabIndex={0}
        >
            <div className="message-content-wrapper">
                {children}
            </div>
            {isStreaming && <div className="message-streaming-overlay" aria-hidden="true" />}
            {showMenu && menuStyle && createPortal(
                <MessageContextMenu items={items} className={menuClassName} style={menuStyle} isDarkTheme={isDarkTheme} />,
                document.body
            )}
        </div>
    )
}

export default MessageWrapper
